import { router } from '@inertiajs/react'
import { GemsView } from '@/components/gems/GemsView'
import type { Gem, Status } from '@/components/gems/types'

interface GemsProps {
  streamId: string
  streamName: string
  gems: Gem[]
  statuses: Status[]
  availableTags: string[]
}

export default function StreamGems({ streamId, streamName, gems, statuses, availableTags }: GemsProps) {
  const handleViewItem = (itemId: string) => {
    router.visit(`/items/${itemId}`)
  }

  const handleViewGem = (gemId: string) => {
    router.visit(`/gems/${gemId}`)
  }

  const handleChangeStatus = (gemId: string, statusId: string) => {
    router.patch(`/gems/${gemId}/update_status`, { status: statusId }, {
      preserveScroll: true,
      onSuccess: () => router.reload({ only: ['gems'] })
    })
  }

  const handleArchive = (gemId: string) => {
    router.post(`/gems/${gemId}/archive`, {}, {
      preserveScroll: true,
      onSuccess: () => router.reload({ only: ['gems'] })
    })
  }

  const handleDelete = (gemId: string) => {
    if (confirm('Are you sure you want to delete this gem?')) {
      router.delete(`/gems/${gemId}`, {
        preserveScroll: true,
        onSuccess: () => router.reload({ only: ['gems'] })
      })
    }
  }

  const handleAddStatus = (name: string, color?: string) => {
    router.post(`/streams/${streamId}/gem_statuses`, {
      gem_status: { name, color: color || 'blue' }
    }, {
      preserveScroll: true,
      onSuccess: () => router.reload({ only: ['statuses'] })
    })
  }

  const handleUpdateStatus = (statusId: string, data: { name?: string; color?: string; isDefault?: boolean }) => {
    if (data.isDefault) {
      router.post(`/streams/${streamId}/gem_statuses/${statusId}/set_default`, {}, {
        preserveScroll: true,
        onSuccess: () => router.reload({ only: ['statuses'] })
      })
    } else {
      router.patch(`/streams/${streamId}/gem_statuses/${statusId}`, {
        gem_status: data
      }, {
        preserveScroll: true,
        onSuccess: () => router.reload({ only: ['statuses'] })
      })
    }
  }

  const handleDeleteStatus = (statusId: string) => {
    router.delete(`/streams/${streamId}/gem_statuses/${statusId}`, {
      preserveScroll: true,
      onSuccess: () => router.reload({ only: ['statuses', 'gems'] })
    })
  }

  const handleReorderStatus = (statusId: string, newPosition: number) => {
    router.patch(`/streams/${streamId}/gem_statuses/${statusId}/reorder`, {
      position: newPosition
    }, {
      preserveScroll: true,
      onSuccess: () => router.reload({ only: ['statuses'] })
    })
  }

  const handleBack = () => {
    router.visit(`/streams/${streamId}`)
  }

  return (
    <GemsView
      gems={gems}
      statuses={statuses}
      availableTags={availableTags}
      streamContext={{ id: streamId, name: streamName }}
      onViewItem={handleViewItem}
      onViewGem={handleViewGem}
      onChangeStatus={handleChangeStatus}
      onArchive={handleArchive}
      onDelete={handleDelete}
      onAddStatus={handleAddStatus}
      onUpdateStatus={handleUpdateStatus}
      onDeleteStatus={handleDeleteStatus}
      onReorderStatus={handleReorderStatus}
      onBack={handleBack}
    />
  )
}
